/*
 * Copyright © 2022 EPO: European Patent Office.
 * ALL RIGHTS RESERVED
 */

import { useDispatch, useSelector } from 'react-redux';
import {
  selectCurrentBioMaterialId,
  selectCurrentBioMaterialAttachments,
  updateCurrentBmAttachments,
  updateCurrentBioMaterialId,
} from './bioMaterials.slice';
import { removeAttachmentsById } from './bioMaterial.thunk';

// TODO: this hook needs a refactor
// it does not work correctly: canceling after an attachment was added or removed should not apply that change
// key to persist in fomrData
const biologicalInfosKey = 'seqlBio.biologicalInfos';
const bioMaterialKey = 'bioMaterial';
const attachmentsKey = 'attachments';

export function useBioMaterials({
  submissionId,
  idSchema,
  registry,
  onEdit,
  onCancel,
  onConfirm,
  onDelete,
  confirmDialogCard,
  closeDialogCard,
}) {
  const dispatch = useDispatch();
  const currentBmAttachments = useSelector(selectCurrentBioMaterialAttachments);
  const currentBioMaterialId = useSelector(selectCurrentBioMaterialId);

  // return input handlers for other array items
  if (!idSchema.$id.includes('biologicalInfos')) {
    return {
      onCancelAction: onCancel,
      onEditAction: onEdit,
      onDeleteConfirm: confirmDialogCard,
      onDeleteAction: onDelete,
      onCloseConfirm: closeDialogCard,
      onConfirmAction: onConfirm,
    };
  }

  const updateCurrentBmAttachmentsOnStore = attachmentsList => dispatch(updateCurrentBmAttachments(attachmentsList));
  const updateCurrentBioMaterialIdOnStore = attachmentsList => dispatch(updateCurrentBioMaterialId(attachmentsList));

  const getBioMaterialArrayIndex = () =>
    registry.getFormData(biologicalInfosKey)?.findIndex(b => b.id === currentBioMaterialId || b.id === undefined);

  const removeBioMaterialAttachments = async (attachments) => {
    if (attachments?.length > 0) {
      const attachmentIds = attachments.map(attachment => attachment.id);
      dispatch(removeAttachmentsById({ submissionId, attachmentIds }));

      let allAttachments = registry.getFormData(attachmentsKey);
      allAttachments = allAttachments.filter(attachment => !attachmentIds.includes(attachment.id));
      await registry.setFormData(allAttachments, attachmentsKey); // this doesnt work well onSectionChange#onSectionChange sees old data in the attachments list
    }
  };

  const cleanupBioMaterials = async (index) => {
    // update section to be empty
    await removeBioMaterialAttachments(currentBmAttachments);
    const bioMaterials = await registry.getFormData(`${biologicalInfosKey}`);
    // eslint-disable-next-line
    bioMaterials?.splice(index, 1);
    // remove all uploaded attachments
    await registry.setFormData(bioMaterials, `${biologicalInfosKey}`);
  };

  const onEditAction = async (id) => {
    await onEdit(id);
    // this is usefull to perform specific actions in  -> list[list[]]
    updateCurrentBioMaterialIdOnStore(id);
    const biologicalInfoItem = registry.getFormData(biologicalInfosKey)?.find(b => b.id === id);
    updateCurrentBmAttachmentsOnStore(biologicalInfoItem?.bioMaterial?.attachments || []);
  };

  const onConfirmAction = async (element) => {
    await onConfirm(element);
    updateCurrentBioMaterialIdOnStore(null);
    updateCurrentBmAttachmentsOnStore([]);
  };

  const onCancelAction = async (arrayItem) => {
    const canceledBioMaterial = registry.getFormData(biologicalInfosKey)[arrayItem.index];
    // if no current => we are in crete mode
    // if we cancel a bio material creation
    // we need to clear attachments already uploaded in db
    await onCancel(arrayItem);
    // TODO: this handling in not correct: canceling after an attachment was added or deleted should
    // if sequence number is present then it means that this bio already existed, and we should not clear attachments
    if (!currentBioMaterialId && !canceledBioMaterial?.sequenceNumber) {
      await cleanupBioMaterials(arrayItem.index);
    }
    updateCurrentBioMaterialIdOnStore(null);
    updateCurrentBmAttachmentsOnStore([]);
  };

  const getEntityAttachments = async () => {
    const currentBioMaterial = await registry.getFormData(biologicalInfosKey)?.find(b => b.id === currentBioMaterialId);
    if (currentBioMaterial) {
      const { attachments = [] } = currentBioMaterial[bioMaterialKey];
      return attachments;
    }
    return null;
  };

  const onDeleteAction = async (id) => {
    updateCurrentBioMaterialIdOnStore(id);
    // without awaits there were some bugs with submission.attachments update through registry.setFormData
    await clearAttachments(id);
    await onDelete(id);
    updateCurrentBioMaterialIdOnStore(null);
    updateCurrentBmAttachmentsOnStore([]);
  };

  const clearAttachments = async (id) => {
    const biologicalInfoItemAttachments = registry.getFormData(biologicalInfosKey)?.find(b => b.id === id)[
      bioMaterialKey
    ][attachmentsKey];
    if (biologicalInfoItemAttachments && biologicalInfoItemAttachments.length > 0) {
      await removeBioMaterialAttachments(biologicalInfoItemAttachments);

      // for some unknown reason we have to reset attachments form data again
      const biologicalInfoItemAttachmentsIds = biologicalInfoItemAttachments.map(attachment => attachment.id);
      let allAttachments = registry.getFormData(attachmentsKey);
      allAttachments = allAttachments.filter(attachment => !biologicalInfoItemAttachmentsIds.includes(attachment.id));
      await registry.setFormData(allAttachments, attachmentsKey);
    }
  };

  const onDeleteConfirm = async () => {
    const attachments = await getEntityAttachments();
    await removeBioMaterialAttachments(attachments);
    updateCurrentBioMaterialIdOnStore(null);
    updateCurrentBmAttachmentsOnStore([]);
  };

  const onCloseConfirm = async () => {
    updateCurrentBioMaterialIdOnStore(null);
    updateCurrentBmAttachmentsOnStore([]);
  };

  return {
    // schema field keys
    biologicalInfosKey,
    bioMaterialKey,
    attachmentsKey,
    // selectors
    currentBioMaterialId,
    currentBmAttachments,
    // redux
    getBioMaterialArrayIndex,
    updateCurrentBmAttachmentsOnStore,
    // array item customized actions
    onEditAction,
    onCancelAction,
    onDeleteAction,
    onConfirmAction,
    onDeleteConfirm,
    onCloseConfirm,
  };
}
